/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { fetchAccounts, fetchAccountDetails, fetchRiskSettings } from "../api/api";
import CustomSelect from "@/components/ui/CustomSelect";
import { useUserPreferences } from "@/context/UserPreferencesContext";

/* ── tiny platform colour map ── */
const platformColors = {
    MT5:     { bg: "bg-blue-500/15", text: "text-blue-400", border: "border-blue-500/30" },
    cTrader: { bg: "bg-purple-500/15", text: "text-purple-400", border: "border-purple-500/30" },
    Rithmic: { bg: "bg-amber-500/15", text: "text-amber-400", border: "border-amber-500/30" },
    Binance: { bg: "bg-yellow-500/15", text: "text-yellow-400", border: "border-yellow-500/30" },
    InteractiveBrokers: { bg: "bg-red-500/15", text: "text-red-400", border: "border-red-500/30" },
};
const defaultPlatformColor = { bg: "bg-slate-500/15", text: "text-slate-400", border: "border-slate-500/30" };

const AccountSelection = ({ 
    selectedAccount, 
    setSelectedAccount, 
    accountBalance, 
    setAccountBalance, 
    accountEquity, 
    setAccountEquity,
    setRiskPercentFromSettings,
    displayMode = "full" // "full" or "compact"
}) => {
    const [accounts, setAccounts] = useState([]);
    const { defaultAccountId } = useUserPreferences() || {};

    useEffect(() => {
        const loadAccounts = async () => {
            try {
                const response = await fetchAccounts();
                const userAccounts = Array.isArray(response) ? response : response.data;
                const list = userAccounts || [];
                setAccounts(list);
                if (list.length > 0 && !selectedAccount) {
                    const backendDefault = list.find(a => a.is_default);
                    const preferred = backendDefault
                        || (defaultAccountId && list.find(a => String(a.id) === String(defaultAccountId)));
                    setSelectedAccount((preferred?.id) ?? list[0].id);
                }
            } catch (error) {
                console.error("Error fetching accounts:", error);
                setAccounts([]);
            }
        };
        loadAccounts();
    }, [defaultAccountId]); 

    useEffect(() => {
        if (selectedAccount && setRiskPercentFromSettings) {
            fetchRiskSettings(selectedAccount)
                .then(settings => {
                    if (settings && typeof settings.risk_percent !== 'undefined') {
                        setRiskPercentFromSettings(settings.risk_percent);
                    }
                })
                .catch(error => console.error("Error fetching risk settings:", error));
        }
    }, [selectedAccount, setRiskPercentFromSettings]);

    /* Build options with metadata for rendering */
    const accountOptions = useMemo(() => accounts.map(account => ({
        value: account.id,
        label: account.name,
        platform: account.platform,
        isDefault: !!account.is_default,
    })), [accounts]);

    /* ── Rich dropdown item ── */
    const renderOption = useCallback((option, { selected }) => {
        const colors = platformColors[option.platform] || defaultPlatformColor;
        return (
            <span className="flex items-center gap-2.5 w-full pr-2 py-0.5">
                <span className={`${selected ? 'font-semibold' : 'font-normal'} min-w-0 truncate`}>
                    {option.label}
                </span>
                <span className="flex items-center gap-1.5 shrink-0 ml-auto">
                    {option.isDefault && (
                        <span className="text-[10px] font-semibold leading-none px-1.5 py-0.5 rounded bg-primary/15 text-primary border border-primary/30">
                            DEFAULT
                        </span>
                    )}
                    <span className={`text-[10px] font-medium leading-none px-1.5 py-0.5 rounded border ${colors.bg} ${colors.text} ${colors.border}`}>
                        {option.platform}
                    </span>
                </span>
            </span>
        );
    }, []);

    /* ── Trigger display (selected value) ── */
    const renderSelected = useCallback((option) => {
        const colors = platformColors[option.platform] || defaultPlatformColor;
        return (
            <span className="flex items-center gap-2 min-w-0 overflow-hidden">
                <span className="truncate min-w-0">{option.label}</span>
                <span className={`shrink-0 text-[10px] font-medium leading-none px-1.5 py-0.5 rounded border ${colors.bg} ${colors.text} ${colors.border}`}>
                    {option.platform}
                </span>
            </span>
        );
    }, []);

    const placeholder = displayMode === "compact"
        ? (accounts.length === 0 ? "Loading..." : "Select Account")
        : "Account:";

    return (
        <div className={`text-textPrimary min-w-0 ${displayMode === "compact" ? "w-auto" : ""}`}>
            <CustomSelect
                options={accountOptions}
                value={selectedAccount || ""}
                onChange={setSelectedAccount}
                placeholder={placeholder}
                className={displayMode === "compact" ? "min-w-0 sm:min-w-[200px] w-full sm:w-auto" : "min-w-0 sm:min-w-[240px] w-full sm:w-auto"}
                itemHeight={48}
                maxDropdownHeight={480}
                renderOption={renderOption}
                renderSelected={renderSelected}
            />
        </div>
    );
};

export default AccountSelection;
